import { RequestBuilder, fetchAndRetryIfServerBusy } from '@cvent/nucleus-networking';
import { fetchWithSessionTimeout } from '../dialogs/SessionTimedOutDialog';
import { mapToArray, arrayToMap } from 'nucleus-widgets/utils/arrayToMap';

import Logger from '@cvent/nucleus-logging';
import { ServiceError } from 'event-widgets/utils/errors';
const LOG = new Logger('RegCartClient');

function isAnswered(answer) {
  return answer && (typeof answer.toJSON !== 'function' || answer.toJSON());
}

/**
 * Function to convert some maps to arrays in the reg cart
 */
function convertRegCart(regCart) {
  return {
    ...regCart,
    eventRegistrations: mapToArray(regCart.eventRegistrations, (erId, er) => ({
      ...er,
      attendee: {
        ...er.attendee,
        eventAnswers: mapToArray(er.attendee.eventAnswers).filter(answer => isAnswered(answer))
          .map(answer => (typeof answer.toJSON === 'function' && answer.toJSON()) ? answer.toJSON() : answer),
        personalInformation: {
          ...er.attendee.personalInformation,
          customFields: mapToArray(er.attendee.personalInformation.customFields)
            .filter(answer => isAnswered(answer))
        }
      }
    }))
  };
}

/**
 * Function to convert api response reg cart back to application's format
 */
function convertRegCartResponse(regCart) {
  return {
    ...regCart,
    eventRegistrations: arrayToMap(regCart.eventRegistrations, er => er.eventRegistrationId, er => ({
      ...er,
      attendee: {
        ...er.attendee,
        eventAnswers: arrayToMap(er.attendee.eventAnswers, answer => answer.questionId),
        personalInformation: {
          ...er.attendee.personalInformation,
          customFields: arrayToMap(er.attendee.personalInformation.customFields, answer => answer.questionId)
        }
      }
    }))
  };
}

/**
 * Function to convert api response back to application's format
 */
function convertResponse(body) {
  return {
    ...body,
    regCart: convertRegCartResponse(body.regCart)
  };
}

const defaultPollingStrategy = {
  quickPollingTries: 3, // How many times to poll at the quick polling interval before falling back to normal
  quickPollingInterval: 1000, // Number of ms to wait between polling calls for the first quickPollingTries times
  normalPollingInterval: 3000 // Number of ms to wait between polling calls after the first quickPollingTries times
};

/**
 * The reg cart service client.
 */
export default class RegCartClient {
  regCartBaseUrl
  paymentBaseUrl
  regCartV2Url
  regCartAuthBaseUrl
  environment
  request
  capacityBaseUrl
  constructor(baseURL, eventId, environment, userType) {
    this.regCartBaseUrl = baseURL + 'registration/v1/regcart';
    this.regCartV2Url = baseURL + 'registration/v2/regcart';
    this.paymentBaseUrl = baseURL + 'registration/v1/paymentProxy';
    this.regCartAuthBaseUrl = baseURL + 'auth';
    this.capacityBaseUrl = baseURL + 'registration/v1/regcart/capacity';
    this.environment = environment;
    this.request = new RequestBuilder({ url: this.regCartBaseUrl }).withCookies();
    this.request = this.request.header('Localized-Validations', 'true')
      .header('Cvent-Event-ID', eventId)
      .header('Cvent-User-Type', userType);
  }

  async getRegCart(authToken, regCartId) {
    const request = this.request
      .url(`${this.regCartBaseUrl}/${regCartId}`)
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error getting reg cart', response, request);
      LOG.info('Error getting reg cart ', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertRegCartResponse(await response.json());
  }

  /**
   * Creates a reg cart
   * @param authToken auth token to use for the request
   * @param regCart the reg cart to create
   * @param contactId the contact id to populate reg cart's user data
   */
  async createRegCart(authToken, regCart, contactId, inviteeId) {
    const request = this.request
      .query('contactId', contactId)
      .query('inviteeId', inviteeId)
      .query('environment', this.environment)
      .post()
      .json(convertRegCart(regCart))
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error creating reg cart', response, request);
      LOG.info('Error creating reg cart ', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * Creates a reg cart from email links
   * @param authToken auth token to use for the request
   * @param inviteeId the invitee id in the email link
   */
  async createRegCartFromLink(authToken, inviteeId, regTypeId, regPathId) {
    const request = this.request
      .url(this.regCartBaseUrl + '/link?inviteeId=' + inviteeId)
      .query('environment', this.environment)
      .query('registrationTypeId', regTypeId)
      .query('registrationPathId', regPathId)
      .post()
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error creating reg cart from link', response, request);
      LOG.info('Error creating reg cart from link', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * Creates a reg cart for test mode events
   * @param authToken auth token to use for the request
   * @param eventId the event id of the test event
   * @param performTest whether to perform test
   */
  async createTestModeRegCartFromLink(authToken, eventId, regTypeId, regPathId) {
    let request = this.request
      .url(this.regCartBaseUrl + '/link')
      .query('eventId', eventId)
      .query('performTest', 'true')
      .query('environment', this.environment);
    if (regTypeId) {
      request = request.query('registrationTypeId', regTypeId);
    }
    if (regPathId) {
      request = request.query('registrationPathId', regPathId);
    }
    request = request.post()
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create(
        'Error creating reg cart from link for test mode event', response, request);
      LOG.info('Error creating reg cart from link for test mode event',
        request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * Updates a reg cart
   * @param authToken auth token to use for the request
   * @param regCart what to update the reg cart to
   */
  async updateRegCart(authToken, regCart, linkedInviteeIdToRemove) {
    let requestToBuild = this.request
      .url(this.regCartBaseUrl + '/' + regCart.regCartId)
      .query('environment', this.environment)
      .put()
      .json(convertRegCart(regCart))
      .auth(authToken);
    if (linkedInviteeIdToRemove) {
      requestToBuild = requestToBuild.query('linkedInviteeIdToRemove', linkedInviteeIdToRemove);
    }
    const request = requestToBuild.build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error updating reg cart', response, request);
      LOG.info('Error updating reg cart', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    const regCartResponse = await response.json();
    return convertResponse(regCartResponse);
  }

  /**
   * Calculate pricing for a reg cart
   * @param authToken auth token to use for the request
   * @param regCartId id of the reg cart
   * @param payment payment information to use for checkout
   */
  async calculateRegCartPricing(authToken, regCartId, pricingInfo) {
    const request = this.request
      .url(this.regCartBaseUrl + '/' + regCartId + '/pricing')
      .query('environment', this.environment)
      .put()
      .json(pricingInfo)
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error retrieving pricing for reg cart', response, request);
      LOG.info('Error retriving pricing for reg cart', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return await response.json();
  }

  async getRegCartPricing(authToken: string, regCartId: string)
    : Promise<{ regCartPricing: RegCartPricing, validationMessages: Array<ValidationMessage> }> {
    const request = this.request
      .url(this.regCartBaseUrl + '/' + regCartId + '/pricing')
      .query('environment', this.environment)
      .get()
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error getting pricing for reg cart', response, request);
      LOG.info('Error retrieving pricing for reg cart', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return await response.json();
  }

  /**
   * Start checkout for a reg cart
   * @param authToken auth token to use for the request
   * @param regCartId id of the reg cart
   * @param payment payment information to use for checkout
   */
  async startRegCartCheckout(authToken, regCartId, pricingInfo, registrationCheckoutTimeout) {
    let requestBuilder = this.request
      .url(this.regCartV2Url + '/' + regCartId + '/checkout')
      .query('environment', this.environment)
      .put()
      .json(pricingInfo)
      .auth(authToken);
    if (registrationCheckoutTimeout) {
      requestBuilder = requestBuilder.query('dequeueTimeout', registrationCheckoutTimeout);
    }
    const request = requestBuilder.build();

    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error checking out reg cart', response, request);
      LOG.info('Error checking out reg cart', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return response.json();
  }

  async acknowledgeRegCartStatus(authToken, regCartId, statusAction) {
    let requestBuilder = this.request
      .url(this.regCartBaseUrl + '/' + regCartId + '/status')
      .query('environment', this.environment)
      .put()
      .json(statusAction)
      .auth(authToken);

    const request = requestBuilder.build();

    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error acknowledging status', response, request);
      LOG.info('Error acknowledging status', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
  }

  /**
   const response = await fetchWithSessionTimeout(request);
   * Delays until the next poll should occur
   * @param pollingStrategy the polling strategy to use for polling
   * @param quickPollingTriesLeft number of tries left using quick polling before switching to normal polling
   */
  waitToPoll(pollingStrategy, quickPollingTriesLeft) {
    return new Promise(resolve => {
      setTimeout(() => resolve(),
        quickPollingTriesLeft > 0 ? pollingStrategy.quickPollingInterval : pollingStrategy.normalPollingInterval);
    });
  }

  /**
   * Polls for reg cart completion with progress updates after each poll
   * @param authToken auth token to use for the request
   * @param regCartId id of the reg cart
   * @param onProgress callback to call whenever getting an update about the reg cart
   * @param pollingStrategy the polling strategy to use for polling
   */
  async waitForRegCartCheckoutCompletion(authToken, regCartId, onProgress, pollingStrategy = defaultPollingStrategy) {
    const pollingRequestBuilder = this.request
      .get()
      .auth(authToken);

    let quickPollingTriesLeft = pollingStrategy.quickPollingTries;
    for (; ;) {
      const request = pollingRequestBuilder
      // append timestamp to stop IE from caching the request
        .url(this.regCartBaseUrl + '/' + regCartId + '/status?ts=' + new Date().getTime())
        .query('environment', this.environment)
        .build();
      const response = await fetchWithSessionTimeout(request);
      const responseCopy = response.clone();
      if (response.ok) {
        LOG.debug('polling for reg cart checkout completion');
        const regCartStatus = await response.json();
        onProgress(regCartStatus);

        switch (regCartStatus.statusCode) {
          case 'THIRD_PARTY_REDIRECT': {
            await this.acknowledgeRegCartStatus(authToken, regCartId, 'UPDATE_REDIRECT_STATUS');
            window.location = regCartStatus.paymentInfo.thirdPartyRedirectUrl;
            return regCartStatus;
          }
          case 'COMPLETED':
          case 'COMPLETED_PENDING_REFUNDS':
            LOG.debug('reg cart checkout completed', regCartStatus);
            return regCartStatus;
          case 'TIME_OUT_EXPIRED':
            LOG.info('Timeout expired for registration', regCartId);
            return regCartStatus;
          case 'FAILED':
          case 'ABANDONED':
          case 'CANCELLED':
            LOG.info('reg cart checkout failed', request.url, regCartStatus);
            throw new Error('reg cart checkout failed');
          case 'INPROGRESS': {
            if (regCartStatus.paymentInfo.paymentStatus === 'PaymentFailed') {
              LOG.debug('reg cart checkout payment processing failed', regCartStatus);
              const error = await ServiceError.create(
                'reg cart checkout payment processing failed',
                responseCopy,
                request);
              LOG.info('reg cart checkout payment processing failed',
                request.url, error.responseStatus, error.responseBody);
              throw error;
            }
            LOG.debug('reg cart checkout not complete', regCartStatus);
            quickPollingTriesLeft--;
            await this.waitToPoll(pollingStrategy, quickPollingTriesLeft);
            break;
          }
          case 'PROCESSING':
          case 'QUEUED':
          case 'PAYMENT_INITIATED':
          default:
            LOG.debug('reg cart checkout not complete', regCartStatus);
            quickPollingTriesLeft--;
            await this.waitToPoll(pollingStrategy, quickPollingTriesLeft);
            break;
        }
      } else {
        const error = await ServiceError.create(
          'Error polling for reg cart status during checkout',
          response,
          request);
        LOG.info('Error polling for reg cart status during checkout',
          request.url, error.responseStatus, error.responseBody);
        throw error;
      }
    }
    // This is unreachable, but flow can't figure that out
    /* eslint-disable no-unreachable */
    throw new Error('stopped polling for reg cart checkout without completing');
    /* eslint-enable no-unreachable */
  }

  async identifyByConfirm(
    authToken, eventId, emailAddress, confirmationNumber) {
    LOG.debug('identifyByConfirm', eventId, emailAddress, confirmationNumber);
    const request = this.request
      .url(`${this.regCartBaseUrl}/identifyByConfirm`)
      .query('environment', this.environment)
      .post()
      .json({ eventId, emailAddress, confirmationNumber })
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);

    if (!response.ok) {
      const error = await ServiceError.create('identifyByConfirm failed', response, request);
      LOG.info('Error retrieve pricing for reg cart', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * Create auth token for starting a reg mod or cancellation
   */
  async authorizeByConfirm(
    authToken,
    eventId,
    inviteeId,
    confirmationNumber,
    emailAddress) {
    LOG.debug('authorizeByConfirm', inviteeId, confirmationNumber);
    const request = this.request
      .url(`${this.regCartAuthBaseUrl}`)
      .query('environment', this.environment)
      .post()
      .json({
        eventId,
        inviteeId,
        confirmationNumber,
        emailAddress
      })
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);

    if (!response.ok) {
      const error = await ServiceError.create('authorizeByConfirm failed', response, request);
      LOG.info('Error creating auth token for invitee', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return await response.json();
  }

  /**
   * Creates reg cart for a reg mod using a token that has been authorized for a user who has already registered
   */
  async createRegModCart(authToken) {
    LOG.debug('createRegModCart');
    const request = this.request
      .url(`${this.regCartBaseUrl}/mod`)
      .query('environment', this.environment)
      .post()
      .json({})
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);

    if (!response.ok) {
      const error = await ServiceError.create('createRegModCart failed', response, request);
      LOG.info('Error starting registration modification', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * Creates reg cart for a cancel registration using a token
   * that has been authorized for a user who has already registered
   */
  async createCancelRegistrationCart(authToken) {
    LOG.debug('createCancelRegistrationCart');
    const request = this.request
      .url(`${this.regCartBaseUrl}/cancel`)
      .query('environment', this.environment)
      .post()
      .json({})
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);

    if (!response.ok) {
      const error = await ServiceError.create('createCancelRegistrationCart failed', response, request);
      LOG.info('Error starting registration cancellation', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * create registrations cart for Decline Survey
   */
  async createDeclineRegistrationCart(
    authToken,
    eventId,
    inviteeId,
    sendEmail
  ) {
    const request = this.request
      .url(`${this.regCartBaseUrl}/decline`)
      .query('eventId', eventId)
      .query('inviteeId', inviteeId)
      .query('environment', this.environment)
      .query('sendEmail', Boolean(sendEmail).toString())
      .post()
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('createDeclineRegistrationCart failed', response, request);
      LOG.info('Error declining registration', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * create registrations cart for Waitlist
   */
  async createWaitlistRegistrationCart(
    authToken,
    eventId,
    inviteeId
  ) {
    const request = this.request
      .url(`${this.regCartBaseUrl}/waitlist`)
      .query('eventId', eventId)
      .query('inviteeId', inviteeId)
      .query('environment', this.environment)
      .post()
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('createWaitlistRegistrationCart failed', response, request);
      LOG.info('Error waitlisting registration', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return convertResponse(await response.json());
  }

  /**
   * gets capacity counts updated with available transfers
   */
  async getCapacitySummaries(authToken, regCartId, capacityIds) {
    const request = this.request
      .url(`${this.capacityBaseUrl}/${regCartId}`)
      .query('environment', this.environment)
      .post()
      .json(capacityIds)
      .auth(authToken)
      .build();
    const response = await fetchWithSessionTimeout(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error getting reg cart capacities', response, request);
      LOG.info('Error getting reg cart capacities', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return await response.json();
  }

  /**
   * gets capacity counts updated with available transfers
   */
  async getCapacitySummariesOnPageLoad(authToken, regCartId, capacityIds) {
    const request = this.request
      .url(`${this.capacityBaseUrl}/${regCartId}`)
      .query('environment', this.environment)
      .post()
      .json(capacityIds)
      .auth(authToken)
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      const error = await ServiceError.create('Error getting reg cart capacities', response, request);
      LOG.info('Error getting reg cart capacities', request.url, error.responseStatus, error.responseBody);
      throw error;
    }
    return await response.json();
  }
}
